import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { mellIcon, usdtIcon } from '../../assets';

import { TRootState } from '../../store/store';
import { transport } from '../../utils/constants';
import { getToken } from '../../utils/helper';
import './Exchange.scss';
import { ExchangerClient } from '../../generated/mlmbox/client/exchanger/exchanger.client';
import { Empty } from '../../generated/google/protobuf/empty';
import { Wallet_Asset } from '../../generated/mlmbox/types/wallet';

interface IExchangerProps {
    mellValue: string;
    usdtValue: string;
    setMellValue: (value: string) => void;
    setUsdtValue: (value: string) => void;
    setOpenAmount: Function;
    assetId: number;
    setAssetId: Function;
    setExchange: Function;
    setReload: Function;
    type: 'sell' | 'buy';
}

const Exchange: FC<IExchangerProps> = ({
    setReload,
    setExchange,
    usdtValue,
    mellValue,
    setMellValue,
    setUsdtValue,
    setOpenAmount,
    assetId,
    type,
}) => {
    const [asset, setAsset] = useState<Wallet_Asset | undefined>();
    // const [revert, setRevert] = useState(false);
    const [fetch, setFetch] = useState(false);
    const assetList = useSelector((state: TRootState) => state.wallet.value.assets);
    const { t } = useTranslation();

    useEffect(() => {
        assetList.forEach((asset) => {
            if (asset.assetId === assetId) setAsset(asset);
        });
    }, [assetId]);

    function previewExchange(value: string) {
        if (fetch) return;

        const exchangerClient = new ExchangerClient(transport);

        exchangerClient
            .all(Empty, { meta: { authorization: getToken() } })
            .then(({ status, response }) => {
                if (status.code === 'OK') {
                    response.items.forEach((item) => {
                        if (item.sourceAssetId === assetId) {
                            exchangerClient
                                .preview(
                                    {
                                        sourceAssetId: item.sourceAssetId,
                                        targetAssetId: item.targetAssetId,
                                        sourceAmount: value,
                                    },
                                    { meta: { authorization: getToken() } }
                                )
                                .then(({ status, response }) => {
                                    if (status.code === 'OK') {
                                        if (assetId === 1) {
                                            setUsdtValue(response.source?.value || '');
                                            setMellValue(response.target?.value || '');
                                        }
                                        if (assetId === 2) {
                                            setMellValue(response.source?.value || '');
                                            setUsdtValue(response.target?.value || '');
                                        }
                                    }
                                    setFetch(false);
                                });
                        }
                    });
                }
            })
            .catch((e) => {
                console.log(e);
                window.Telegram.WebApp.HapticFeedback.notificationOccurred('warning');
            })
            .finally(() => setFetch(false));
    }

    function verifyExchange(value: string) {
        if (fetch) return;
        setFetch(true);
        const exchangerClient = new ExchangerClient(transport);

        exchangerClient
            .all(Empty, { meta: { authorization: getToken() } })
            .then(({ status, response }) => {
                if (status.code === 'OK') {
                    response.items.forEach((item) => {
                        if (item.sourceAssetId === assetId) {
                            exchangerClient
                                .verified(
                                    {
                                        sourceAmount: value,
                                        sourceAssetId: item.sourceAssetId,
                                        targetAssetId: item.targetAssetId,
                                    },
                                    { meta: { authorization: getToken() } }
                                )
                                .then(({ status }) => {
                                    if (status.code === 'OK') {
                                        setReload(true);
                                        setExchange(false);
                                    }
                                    setFetch(false);
                                });
                        }
                    });
                }
            })
            .catch((e) => {
                console.log(e);
                window.Telegram.WebApp.HapticFeedback.notificationOccurred('warning');
            })
            .finally(() => setFetch(false));
    }

    return (
        <>
            <div className="exchange">
                <div className="exchange__asset">
                    <div className="exchange__asset--inner">
                        <div className="exchange__asset--inner--header">
                            <div className="info">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none">
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M12.9133 10.8695C12.9407 10.7689 12.8645 10.6723 12.7639 10.675H11.061C9.14717 10.675 7.46022 9.10637 7.46022 7C7.46022 4.89364 9.14717 3.32501 11.061 3.32501L12.7639 3.325C12.8645 3.32771 12.9407 3.23103 12.9133 3.13053C12.7779 2.63182 12.5629 2.20676 12.218 1.84887C11.713 1.32502 11.0728 1.09253 10.2817 0.982199C9.51312 0.87498 8.53103 0.874987 7.29107 0.875H5.86556C4.62562 0.874987 3.6435 0.87498 2.87488 0.982199C2.08385 1.09253 1.44359 1.32502 0.938669 1.84887C0.433751 2.37273 0.20967 3.03699 0.103324 3.85769C-1.9625e-05 4.65513 -1.29076e-05 5.67408 2.96012e-07 6.96052V7.03948C-1.29076e-05 8.32594 -1.9625e-05 9.34486 0.103324 10.1423C0.20967 10.963 0.433751 11.6273 0.938669 12.1512C1.44359 12.675 2.08385 12.9074 2.87488 13.0178C3.6435 13.125 4.62562 13.125 5.86556 13.125H7.29107C8.53103 13.125 9.51312 13.125 10.2817 13.0178C11.0728 12.9074 11.713 12.675 12.218 12.1512C12.5629 11.7933 12.7779 11.3681 12.9133 10.8695ZM2.86747 9.1C2.86747 9.38994 3.09403 9.625 3.3735 9.625C3.65296 9.625 3.87952 9.38994 3.87952 9.1V4.9C3.87952 4.61005 3.65296 4.375 3.3735 4.375C3.09403 4.375 2.86747 4.61005 2.86747 4.9V9.1Z"
                                        fill="#8993A7"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M12.7644 4.375C12.8075 4.37499 12.8491 4.37497 12.8874 4.37747C13.454 4.4146 13.9551 4.87467 13.9973 5.51726C14 5.55841 14 5.60269 14 5.64395V8.35607C14 8.3973 14 8.44159 13.9973 8.48274C13.9551 9.12534 13.454 9.58538 12.8874 9.62255C12.8501 9.625 12.8097 9.625 12.7678 9.625H11.0611C9.666 9.625 8.47233 8.48568 8.47233 7C8.47233 5.5143 9.666 4.37501 11.0611 4.37501L12.7644 4.375ZM11.5613 7C11.5613 7.38661 11.2702 7.7 10.911 7.7C10.5519 7.7 10.2607 7.38661 10.2607 7C10.2607 6.61339 10.5519 6.3 10.911 6.3C11.2702 6.3 11.5613 6.61339 11.5613 7Z"
                                        fill="#8993A7"
                                    />
                                </svg>

                                <p>
                                    {t('wallet.titles.balance')}: {Number(asset?.assetBalance).toLocaleString() || 0}
                                </p>
                            </div>
                            <div
                                className="max"
                                onClick={() => {
                                    window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
                                    asset?.assetBalance && previewExchange(asset.assetBalance);
                                }}>
                                {t('max')}
                            </div>
                        </div>
                        <h1>{t('asset_1')}</h1>
                        <div
                            className="exchange__asset--inner--data"
                            onClick={() => setOpenAmount()}>
                            <div className="title">
                                {assetId === 1 ? <img src={usdtIcon} /> : <img src={mellIcon} />}
                                <h2>{assetId === 1 ? 'USDT' : '$MOON'}</h2>
                            </div>
                            <p className={assetId === 1 ? (Number(usdtValue) <= 0 ? 'none' : '') : Number(mellValue) <= 0 ? 'none' : ''}>
                                {assetId === 1 ? Number(usdtValue).toLocaleString() : Number(mellValue).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
                {/* <div className="revert">
                    <div
                        className={`revert__circle ${revert ? (assetId === 1 ? 'rotate-end' : 'rotate-start') : ''}`}
                        onClick={() => {
                            window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
                            setRevert(true);
                            setMellValue('');
                            setUsdtValue('');
                            if (assetId === 1) {
                                setAssetId(2);
                            } else {
                                setAssetId(1);
                            }
                            console.log(type);
                            if (type === 'buy') setExchange('sell');
                            if (type === 'sell') setExchange('buy');
                        }}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none">
                            <path
                                d="M0.0466758 10.0371C0.149185 9.77354 0.402963 9.60001 0.685742 9.60001L5.48573 9.60001L5.48573 0.685741C5.48573 0.307045 5.79274 2.74033e-05 6.17142 2.73868e-05C6.55011 2.73702e-05 6.85713 0.307045 6.85713 0.685741L6.85713 15.3143C6.85713 15.5862 6.69649 15.8324 6.44753 15.9419C6.19866 16.0514 5.90854 16.0035 5.7081 15.8197L0.222392 10.7912C0.0139354 10.6001 -0.0558429 10.3007 0.0466758 10.0371Z"
                                fill="#252C3F"
                            />
                            <path
                                d="M10.5143 6.40002L15.3143 6.40002C15.5971 6.40002 15.8509 6.2264 15.9533 5.9629C16.0558 5.69932 15.9861 5.39989 15.7776 5.2088L10.2919 0.180242C10.0915 -0.00352904 9.80141 -0.0514397 9.55245 0.0580917C9.30358 0.167532 9.14285 0.413841 9.14285 0.685749L9.14285 15.3143C9.14285 15.693 9.44987 16 9.82856 16C10.2073 16 10.5143 15.693 10.5143 15.3143L10.5143 6.40002Z"
                                fill="#252C3F"
                            />
                        </svg>
                    </div>
                </div> */}
                <div className="exchange__asset">
                    <div className="exchange__asset--inner">
                        <h1>{t('asset_2')}</h1>
                        <div className="exchange__asset--inner--data">
                            <div className="title">
                                {assetId === 2 ? <img src={usdtIcon} /> : <img src={mellIcon} />}
                                <h2>{assetId === 2 ? 'USDT' : '$MOON'}</h2>
                            </div>
                            <p className={assetId === 2 ? (Number(usdtValue) <= 0 ? 'none' : '') : Number(mellValue) <= 0 ? 'none' : ''}>
                                {assetId === 2 ? Number(usdtValue).toLocaleString() : Number(mellValue).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => verifyExchange(assetId === 1 ? usdtValue : mellValue)}
                    style={{ opacity: fetch ? 0.5 : 1 }}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none">
                        <path
                            d="M0.0466758 10.0371C0.149185 9.77354 0.402963 9.60001 0.685742 9.60001L5.48573 9.60001L5.48573 0.685741C5.48573 0.307045 5.79274 2.74033e-05 6.17142 2.73868e-05C6.55011 2.73702e-05 6.85713 0.307045 6.85713 0.685741L6.85713 15.3143C6.85713 15.5862 6.69649 15.8324 6.44753 15.9419C6.19866 16.0514 5.90854 16.0035 5.7081 15.8197L0.222392 10.7912C0.0139354 10.6001 -0.0558429 10.3007 0.0466758 10.0371Z"
                            fill="#252C3F"
                        />
                        <path
                            d="M10.5143 6.40002L15.3143 6.40002C15.5971 6.40002 15.8509 6.2264 15.9533 5.9629C16.0558 5.69932 15.9861 5.39989 15.7776 5.2088L10.2919 0.180242C10.0915 -0.00352904 9.80141 -0.0514397 9.55245 0.0580917C9.30358 0.167532 9.14285 0.413841 9.14285 0.685749L9.14285 15.3143C9.14285 15.693 9.44987 16 9.82856 16C10.2073 16 10.5143 15.693 10.5143 15.3143L10.5143 6.40002Z"
                            fill="#252C3F"
                        />
                    </svg>
                    {type === 'buy' ? t('buy_b') : t('sell_b')}
                </button>
            </div>
        </>
    );
};

export default Exchange;
